import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Phone, Zap, FileText, PlugZap, ClipboardCheck } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { CredentialsSection } from "@/components/CredentialsSection";
import { company } from "@/data/site";
import solarEnergyImg from "@/assets/mckay/solar-energy.webp.asset.json";

const utilities = [
  {
    name: "SDG&E",
    area: "San Diego County and southern Orange County",
    body: "Most of our San Diego work interconnects through SDG&E, so we know how their application queue behaves, what the meter swap process looks like, and how long permission to operate usually takes. Nearly every new system here lands on NEM 3.0, which changes how much a battery matters for your bill.",
    notes: ["NEM 3.0 export credits make storage worth pricing out", "Meter and panel upgrades coordinated with the city permit", "We file the interconnection paperwork for you"],
  },
  {
    name: "SCE / Southern California Edison",
    area: "Orange County, Riverside County, and much of LA County",
    body: "Edison territory covers a wide spread of jurisdictions, and the building department you deal with changes far more than the utility rules do. We confirm which AHJ applies to your address before filing so an install does not stall between two agencies.",
    notes: ["Rate plan review before we finalize a design", "Service panel capacity checked early", "Battery backup sized around outage and PSPS risk"],
  },
  {
    name: "LADWP",
    area: "City of Los Angeles",
    body: "LADWP runs its own interconnection and inspection process separate from the investor-owned utilities, with its own timelines and forms. If your home is served by LADWP, we plan for that schedule up front instead of promising a date the process cannot support.",
    notes: ["LADWP-specific interconnection forms handled in house", "Separate inspection sequence planned into the timeline", "Net metering rules explained in plain numbers"],
  },
  {
    name: "Riverside utilities",
    area: "Riverside Public Utilities, IID, and surrounding municipal providers",
    body: "Inland Empire homes may sit with a municipal provider rather than Edison, and the export rules and rebate programs are not the same. We check who actually bills you before we model savings, because guessing here is how projections end up wrong.",
    notes: ["Municipal versus investor-owned rules confirmed first", "Local rebate and program availability checked", "Heat-driven production modeling for inland roofs"],
  },
  {
    name: "PG&E",
    area: "Northern and central California properties",
    body: "Our home base is Southern California, but we occasionally support owners with property in PG&E territory. If that is you, tell us up front and we will be honest about whether we are the right fit or whether a local crew serves you better.",
    notes: ["Honest scoping before we take the job", "Same design and documentation standards", "Referral if travel makes the project impractical"],
  },
];

export const Route = createFileRoute("/utility-companies")({
  head: () => ({
    meta: [
      { title: "Solar by Utility Company in Southern California – McKay Roofing & Solar" },
      { name: "description", content: "How solar interconnection, net metering and rebates work with SDG&E, SCE/Edison, LADWP, Riverside utilities and PG&E. McKay handles the paperwork." },
      { name: "keywords", content: "SDG&E solar interconnection, SCE Edison solar, LADWP solar net metering, Riverside Public Utilities solar, NEM 3.0 Southern California" },
      { property: "og:title", content: "Solar by Utility Company – McKay Roofing & Solar" },
      { property: "og:description", content: "Interconnection, net metering and rebate guidance for SDG&E, SCE, LADWP, Riverside utilities and PG&E." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/utility-companies" }],
  }),
  component: UtilitiesPage,
});

function UtilitiesPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative min-h-[85vh] w-full overflow-hidden text-primary-foreground">
          <HeroBackground src={solarEnergyImg.url} alt="Home solar array connected to the utility grid in Southern California" overlay="black-soft" />
          <div className="relative flex h-full min-h-[85vh] items-center">
            <div className="mx-auto w-full max-w-7xl px-4 py-24 sm:px-6 lg:px-8">
              <div className="max-w-3xl">
                <h1 className="text-4xl font-bold leading-[1.1] sm:text-5xl lg:text-6xl text-balance">
                  Solar that works with your utility, whoever bills you
                </h1>
                <p className="mt-6 max-w-2xl text-base text-secondary-foreground/85 sm:text-lg">
                  Interconnection, net metering and rebates look different depending on who serves your address. We handle the filing and explain what your rules actually mean for your bill.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-elegant">
                    <Link to="/contact">Get My Free Estimate <ArrowRight className="ml-1 h-4 w-4" /></Link>
                  </Button>
                  <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                    <Link to="/contact">
                      <Phone className="mr-2 h-4 w-4" />
                      {company.phone}
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background">
          <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
            <div className="text-sm font-bold uppercase tracking-wider text-secondary">Utility Specialties</div>
            <h2 className="mt-2 text-3xl font-bold text-primary sm:text-4xl text-balance">One design process, five sets of utility rules</h2>
            <p className="mt-6 text-base leading-relaxed text-foreground/85 sm:text-lg">
              Southern California is served by a mix of investor-owned and municipal utilities, and each one has its own interconnection queue, export credit structure and inspection sequence. We confirm who serves your address first, then design and file around those rules instead of a generic template.
            </p>
          </div>
        </section>

        <section className="bg-muted/40">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <div className="grid gap-6 lg:grid-cols-2">
              {utilities.map((u) => (
                <div key={u.name} className="rounded-2xl border border-border bg-card p-6">
                  <div className="flex items-center gap-3">
                    <Zap className="h-7 w-7 text-primary" />
                    <div>
                      <h3 className="text-xl font-extrabold text-primary">{u.name}</h3>
                      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{u.area}</div>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-foreground/85">{u.body}</p>
                  <ul className="mt-4 space-y-2">
                    {u.notes.map((n) => (
                      <li key={n} className="flex items-start gap-3 text-sm text-muted-foreground">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-secondary" />
                        <span>{n}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background">
          <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
            <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">What we handle on your behalf</h2>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { icon: FileText, t: "Interconnection filing", d: "Applications, single-line diagrams and utility forms submitted and tracked to approval." },
                { icon: ClipboardCheck, t: "Permits and inspection", d: "City or county permit plus the utility inspection sequence, scheduled in the right order." },
                { icon: PlugZap, t: "Meter and panel work", d: "Service capacity checked early so a panel upgrade does not surprise you mid project." },
                { icon: Zap, t: "Permission to operate", d: "We follow the file until your system is cleared to turn on and producing." },
              ].map((f) => (
                <div key={f.t} className="rounded-2xl border border-border bg-card p-5">
                  <f.icon className="h-8 w-8 text-primary" />
                  <div className="mt-4 text-lg font-bold text-primary">{f.t}</div>
                  <p className="mt-1 text-sm text-muted-foreground">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <CredentialsSection className="bg-muted/40" />

        <section className="bg-background">
          <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
            <h2 className="text-3xl font-bold text-primary sm:text-4xl text-balance">Not sure which rules apply to your address?</h2>
            <p className="mx-auto mt-4 max-w-2xl text-muted-foreground">
              Send us your address and a recent bill. We will tell you who serves you, what your export rules look like, and whether storage changes the math.
            </p>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-elegant">
                <Link to="/contact">Get My Free Estimate <ArrowRight className="ml-1 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-glow">
                <Link to="/contact">
                  <Phone className="mr-2 h-4 w-4" />
                  {company.phone}
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
